/**
 * @author mrdoob / http://mrdoob.com/
 * @author *kile / http://kile.stravaganza.org/
 * @author philogb / http://blog.thejit.org/
 * @author mikael emtinger / http://gomo.se/
 * @author egraether / http://egraether.com/
 * @author WestLangley / http://github.com/WestLangley
 */
/* Pruned version of THREE.Vector3, for use in the LMV web worker */

/**
 * A 3D vector with components x, y and z.
 * @constructor
 * @class
 * @memberof Autodesk.Viewing.Math
 *
 * @param {number} x - The x component.
 * @param {number} y - The y component.
 * @param {number} z - The z component.
 *
 * @alias Autodesk.Viewing.Math.Vector3
 */
export function LmvVector3(x, y, z) {
  /**
   * The x component of the vector.
   * @type {number}
   * */
  this.x = x || 0;
  /**
   * The y component of the vector.
   * @type {number}
   * */
  this.y = y || 0;
  /**
   * The z component of the vector.
   * @type {number}
   * */
  this.z = z || 0;
}

LmvVector3.prototype.constructor = LmvVector3;

/**
 * Set the components of this vector.
 * @param {number} x - The x component.
 * @param {number} y - The y component.
 * @param {number} z - The z component.
 * @return {Autodesk.Viewing.Math.Vector3} A reference to this vector.
 *
 * @alias Autodesk.Viewing.Math.Vector3#set
 */
LmvVector3.prototype.set = function (x, y, z) {

  this.x = x;
  this.y = y;
  this.z = z;

  return this;

};

/**
 * Set the x component of this vector.
 * @param {number} x - The x component.
 * @return {Autodesk.Viewing.Math.Vector3} A reference to this vector.
 *
 * @alias Autodesk.Viewing.Math.Vector3#setX
 */
LmvVector3.prototype.setX = function (x) {

  this.x = x;

  return this;

};

/**
 * Set the y component of this vector.
 * @param {number} y - The y component.
 * @return {Autodesk.Viewing.Math.Vector3} A reference to this vector.
 *
 * @alias Autodesk.Viewing.Math.Vector3#setY
 */
LmvVector3.prototype.setY = function (y) {

  this.y = y;

  return this;

};

/**
 * Set the z component of this vector.
 * @param {number} z - The z component.
 * @return {Autodesk.Viewing.Math.Vector3} A reference to this vector.
 *
 * @alias Autodesk.Viewing.Math.Vector3#setZ
 */
LmvVector3.prototype.setZ = function (z) {

  this.z = z;

  return this;

};

/**
 * Set a component by index.
 * @param {number} index - The component index (0=x, 1=y, 2=z).
 * @param {number} value - The value to set.
 *
 * @alias Autodesk.Viewing.Math.Vector3#setComponent
 */
LmvVector3.prototype.setComponent = function (index, value) {

  switch (index) {

    case 0:this.x = value;break;
    case 1:this.y = value;break;
    case 2:this.z = value;break;
    default:throw new Error('index is out of range: ' + index);

  }

};

/**
 * Get a component by index.
 * @param {number} index - The component index (0=x, 1=y, 2=z).
 * @return {number} The component value.
 *
 * @alias Autodesk.Viewing.Math.Vector3#getComponent
 */
LmvVector3.prototype.getComponent = function (index) {

  switch (index) {

    case 0:return this.x;
    case 1:return this.y;
    case 2:return this.z;
    default:throw new Error('index is out of range: ' + index);

  }

};

/**
 * Create a clone of this vector.
 * @return {Autodesk.Viewing.Math.Vector3} A new vector with the same components.
 *
 * @alias Autodesk.Viewing.Math.Vector3#clone
 */
LmvVector3.prototype.clone = function () {

  return new this.constructor(this.x, this.y, this.z);

};

/**
 * Copy the components from another vector.
 * @param {Autodesk.Viewing.Math.Vector3} v - The vector to copy.
 * @return {Autodesk.Viewing.Math.Vector3} A reference to this vector.
 *
 * @alias Autodesk.Viewing.Math.Vector3#copy
 */
LmvVector3.prototype.copy = function (v) {

  this.x = v.x;
  this.y = v.y;
  this.z = v.z;

  return this;

};

/**
 * Add a vector to this vector.
 * @param {Autodesk.Viewing.Math.Vector3} v - The vector to add.
 * @note The second parameter 'w' is deprecated. We omit it from the documentation.
 * @return {Autodesk.Viewing.Math.Vector3} A reference to this vector.
 *
 * @alias Autodesk.Viewing.Math.Vector3#add
 */
LmvVector3.prototype.add = function (v, w) {

  if (w !== undefined) {

    console.warn('THREE.Vector3: .add() now only accepts one argument. Use .addVectors( a, b ) instead.');
    return this.addVectors(v, w);

  }

  this.x += v.x;
  this.y += v.y;
  this.z += v.z;

  return this;

};

/**
 * Add a scalar to each component of this vector.
 * @param {number} s - The scalar to add.
 * @return {Autodesk.Viewing.Math.Vector3} A reference to this vector.
 *
 * @alias Autodesk.Viewing.Math.Vector3#addScalar
 */
LmvVector3.prototype.addScalar = function (s) {

  this.x += s;
  this.y += s;
  this.z += s;

  return this;

};

/**
 * Set this vector to the sum of two vectors.
 * @param {Autodesk.Viewing.Math.Vector3} a - The first vector.
 * @param {Autodesk.Viewing.Math.Vector3} b - The second vector.
 * @return {Autodesk.Viewing.Math.Vector3} A reference to this vector.
 *
 * @alias Autodesk.Viewing.Math.Vector3#addVectors
 */
LmvVector3.prototype.addVectors = function (a, b) {

  this.x = a.x + b.x;
  this.y = a.y + b.y;
  this.z = a.z + b.z;

  return this;

};

/**
 * Add a scaled vector to this vector.
 * @param {Autodesk.Viewing.Math.Vector3} v - The vector to scale and add.
 * @param {number} s - The scale factor.
 * @return {Autodesk.Viewing.Math.Vector3} A reference to this vector.
 *
 * @alias Autodesk.Viewing.Math.Vector3#addScaledVector
 */
LmvVector3.prototype.addScaledVector = function (v, s) {

  this.x += v.x * s;
  this.y += v.y * s;
  this.z += v.z * s;

  return this;

};

/**
 * Subtract a vector from this vector.
 * @param {Autodesk.Viewing.Math.Vector3} v - The vector to subtract.
 * @note The second parameter 'w' is deprecated. We omit it from the documentation.
 * @return {Autodesk.Viewing.Math.Vector3} A reference to this vector.
 *
 * @alias Autodesk.Viewing.Math.Vector3#sub
 */
LmvVector3.prototype.sub = function (v, w) {

  if (w !== undefined) {

    console.warn('THREE.Vector3: .sub() now only accepts one argument. Use .subVectors( a, b ) instead.');
    return this.subVectors(v, w);

  }

  this.x -= v.x;
  this.y -= v.y;
  this.z -= v.z;

  return this;

};

/**
 * Subtract a scalar from each component of this vector.
 * @param {number} s - The scalar to subtract.
 * @return {Autodesk.Viewing.Math.Vector3} A reference to this vector.
 *
 * @alias Autodesk.Viewing.Math.Vector3#subScalar
 */
LmvVector3.prototype.subScalar = function (s) {

  this.x -= s;
  this.y -= s;
  this.z -= s;

  return this;

};

/**
 * Set this vector to the difference of two vectors.
 * @param {Autodesk.Viewing.Math.Vector3} a - The first vector.
 * @param {Autodesk.Viewing.Math.Vector3} b - The second vector.
 * @return {Autodesk.Viewing.Math.Vector3} A reference to this vector.
 *
 * @alias Autodesk.Viewing.Math.Vector3#subVectors
 */
LmvVector3.prototype.subVectors = function (a, b) {

  this.x = a.x - b.x;
  this.y = a.y - b.y;
  this.z = a.z - b.z;

  return this;

};

/**
 * Multiply this vector by another vector component-wise.
 * @param {Autodesk.Viewing.Math.Vector3} v - The vector to multiply by.
 * @note The second parameter 'w' is deprecated. We omit it from the documentation.
 * @return {Autodesk.Viewing.Math.Vector3} A reference to this vector.
 *
 * @alias Autodesk.Viewing.Math.Vector3#multiply
 */
LmvVector3.prototype.multiply = function (v, w) {

  if (w !== undefined) {

    console.warn('THREE.Vector3: .multiply() now only accepts one argument. Use .multiplyVectors( a, b ) instead.');
    return this.multiplyVectors(v, w);

  }

  this.x *= v.x;
  this.y *= v.y;
  this.z *= v.z;

  return this;

};

/**
 * Multiply this vector by a scalar.
 * @param {number} scalar - The scalar to multiply by.
 * @return {Autodesk.Viewing.Math.Vector3} A reference to this vector.
 *
 * @alias Autodesk.Viewing.Math.Vector3#multiplyScalar
 */
LmvVector3.prototype.multiplyScalar = function (scalar) {

  this.x *= scalar;
  this.y *= scalar;
  this.z *= scalar;

  return this;

};

/**
 * Set this vector to the component-wise product of two vectors.
 * @param {Autodesk.Viewing.Math.Vector3} a - The first vector.
 * @param {Autodesk.Viewing.Math.Vector3} b - The second vector.
 * @return {Autodesk.Viewing.Math.Vector3} A reference to this vector.
 *
 * @alias Autodesk.Viewing.Math.Vector3#multiplyVectors
 */
LmvVector3.prototype.multiplyVectors = function (a, b) {

  this.x = a.x * b.x;
  this.y = a.y * b.y;
  this.z = a.z * b.z;

  return this;

};

/**
 * Apply a 3x3 matrix to this vector.
 * @param {Autodesk.Viewing.Math.Matrix3} m - The matrix to apply.
 * @return {Autodesk.Viewing.Math.Vector3} A reference to this vector.
 *
 * @alias Autodesk.Viewing.Math.Vector3#applyMatrix3
 */
LmvVector3.prototype.applyMatrix3 = function (m) {

  var x = this.x;
  var y = this.y;
  var z = this.z;

  var e = m.elements;

  this.x = e[0] * x + e[3] * y + e[6] * z;
  this.y = e[1] * x + e[4] * y + e[7] * z;
  this.z = e[2] * x + e[5] * y + e[8] * z;

  return this;

};

/**
 * Apply a 4x4 affine matrix to this vector.
 * @param {Autodesk.Viewing.Math.Matrix4} m - The affine matrix to apply.
 * @return {Autodesk.Viewing.Math.Vector3} A reference to this vector.
 *
 * @alias Autodesk.Viewing.Math.Vector3#applyMatrix4
 */
LmvVector3.prototype.applyMatrix4 = function (m) {

  // input: THREE.Matrix4 affine matrix

  var x = this.x,y = this.y,z = this.z;

  var e = m.elements;

  this.x = e[0] * x + e[4] * y + e[8] * z + e[12];
  this.y = e[1] * x + e[5] * y + e[9] * z + e[13];
  this.z = e[2] * x + e[6] * y + e[10] * z + e[14];

  return this;

};

/**
 * Apply a 4x4 projection matrix to this vector.
 * @param {Autodesk.Viewing.Math.Matrix4} m - The projection matrix to apply.
 * @return {Autodesk.Viewing.Math.Vector3} A reference to this vector.
 *
 * @alias Autodesk.Viewing.Math.Vector3#applyProjection
 */
LmvVector3.prototype.applyProjection = function (m) {

  // input: THREE.Matrix4 projection matrix

  var x = this.x,y = this.y,z = this.z;

  var e = m.elements;
  var d = 1 / (e[3] * x + e[7] * y + e[11] * z + e[15]); // perspective divide

  this.x = (e[0] * x + e[4] * y + e[8] * z + e[12]) * d;
  this.y = (e[1] * x + e[5] * y + e[9] * z + e[13]) * d;
  this.z = (e[2] * x + e[6] * y + e[10] * z + e[14]) * d;

  return this;

};

/**
 * Apply a quaternion rotation to this vector.
 * @param {Autodesk.Viewing.Math.Quaternion} q - The quaternion to apply.
 * @return {Autodesk.Viewing.Math.Vector3} A reference to this vector.
 *
 * @alias Autodesk.Viewing.Math.Vector3#applyQuaternion
 */
LmvVector3.prototype.applyQuaternion = function (q) {

  var x = this.x;
  var y = this.y;
  var z = this.z;

  var qx = q.x;
  var qy = q.y;
  var qz = q.z;
  var qw = q.w;

  // calculate quat * vector

  var ix = qw * x + qy * z - qz * y;
  var iy = qw * y + qz * x - qx * z;
  var iz = qw * z + qx * y - qy * x;
  var iw = -qx * x - qy * y - qz * z;

  // calculate result * inverse quat

  this.x = ix * qw + iw * -qx + iy * -qz - iz * -qy;
  this.y = iy * qw + iw * -qy + iz * -qx - ix * -qz;
  this.z = iz * qw + iw * -qz + ix * -qy - iy * -qx;

  return this;

};

/**
 * Transform this vector interpreted as a direction by a 4x4 affine matrix.
 * @param {Autodesk.Viewing.Math.Matrix4} m - The affine matrix to apply.
 * @return {Autodesk.Viewing.Math.Vector3} A reference to this vector.
 *
 * @alias Autodesk.Viewing.Math.Vector3#transformDirection
 */
LmvVector3.prototype.transformDirection = function (m) {

  // input: THREE.Matrix4 affine matrix
  // vector interpreted as a direction

  var x = this.x,y = this.y,z = this.z;

  var e = m.elements;

  this.x = e[0] * x + e[4] * y + e[8] * z;
  this.y = e[1] * x + e[5] * y + e[9] * z;
  this.z = e[2] * x + e[6] * y + e[10] * z;

  this.normalize();

  return this;

};

/**
 * Divide this vector by another vector component-wise.
 * @param {Autodesk.Viewing.Math.Vector3} v - The vector to divide by.
 * @return {Autodesk.Viewing.Math.Vector3} A reference to this vector.
 *
 * @alias Autodesk.Viewing.Math.Vector3#divide
 */
LmvVector3.prototype.divide = function (v) {

  this.x /= v.x;
  this.y /= v.y;
  this.z /= v.z;

  return this;

};

/**
 * Divide this vector by a scalar.
 * @param {number} scalar - The scalar to divide by.
 * @return {Autodesk.Viewing.Math.Vector3} A reference to this vector.
 *
 * @alias Autodesk.Viewing.Math.Vector3#divideScalar
 */
LmvVector3.prototype.divideScalar = function (scalar) {

  if (scalar !== 0) {

    var invScalar = 1 / scalar;

    this.x *= invScalar;
    this.y *= invScalar;
    this.z *= invScalar;

  } else {

    this.x = 0;
    this.y = 0;
    this.z = 0;

  }

  return this;

};

/**
 * Set each component to the minimum of this vector and another vector.
 * @param {Autodesk.Viewing.Math.Vector3} v - The other vector.
 * @return {Autodesk.Viewing.Math.Vector3} A reference to this vector.
 *
 * @alias Autodesk.Viewing.Math.Vector3#min
 */
LmvVector3.prototype.min = function (v) {

  if (this.x > v.x) {

    this.x = v.x;

  }

  if (this.y > v.y) {

    this.y = v.y;

  }

  if (this.z > v.z) {

    this.z = v.z;

  }

  return this;

};

/**
 * Set each component to the maximum of this vector and another vector.
 * @param {Autodesk.Viewing.Math.Vector3} v - The other vector.
 * @return {Autodesk.Viewing.Math.Vector3} A reference to this vector.
 *
 * @alias Autodesk.Viewing.Math.Vector3#max
 */
LmvVector3.prototype.max = function (v) {

  if (this.x < v.x) {

    this.x = v.x;

  }

  if (this.y < v.y) {

    this.y = v.y;

  }

  if (this.z < v.z) {

    this.z = v.z;

  }

  return this;

};

/**
 * Clamp this vector between min and max vectors.
 * @param {Autodesk.Viewing.Math.Vector3} min - The minimum values.
 * @param {Autodesk.Viewing.Math.Vector3} max - The maximum values.
 * @return {Autodesk.Viewing.Math.Vector3} A reference to this vector.
 *
 * @alias Autodesk.Viewing.Math.Vector3#clamp
 */
LmvVector3.prototype.clamp = function (min, max) {

  // This function assumes min < max, if this assumption isn't true it will not operate correctly

  if (this.x < min.x) {

    this.x = min.x;

  } else if (this.x > max.x) {

    this.x = max.x;

  }

  if (this.y < min.y) {

    this.y = min.y;

  } else if (this.y > max.y) {

    this.y = max.y;

  }

  if (this.z < min.z) {

    this.z = min.z;

  } else if (this.z > max.z) {

    this.z = max.z;

  }

  return this;

};

/**
 * Clamp each component between scalar min and max values.
 * @function
 * @param {number} minVal - The minimum scalar value.
 * @param {number} maxVal - The maximum scalar value.
 * @return {Autodesk.Viewing.Math.Vector3} A reference to this vector.
 *
 * @alias Autodesk.Viewing.Math.Vector3#clampScalar
 */
LmvVector3.prototype.clampScalar = function () {

  var min, max;

  return function clampScalar(minVal, maxVal) {

    if (min === undefined) {

      min = new LmvVector3();
      max = new LmvVector3();

    }

    min.set(minVal, minVal, minVal);
    max.set(maxVal, maxVal, maxVal);

    return this.clamp(min, max);

  };

}(),

/**
 * Apply Math.floor to each component of this vector.
 * @return {Autodesk.Viewing.Math.Vector3} A reference to this vector.
 *
 * @alias Autodesk.Viewing.Math.Vector3#floor
 */
LmvVector3.prototype.floor = function () {

  this.x = Math.floor(this.x);
  this.y = Math.floor(this.y);
  this.z = Math.floor(this.z);

  return this;

};

/**
 * Apply Math.ceil to each component of this vector.
 * @return {Autodesk.Viewing.Math.Vector3} A reference to this vector.
 *
 * @alias Autodesk.Viewing.Math.Vector3#ceil
 */
LmvVector3.prototype.ceil = function () {

  this.x = Math.ceil(this.x);
  this.y = Math.ceil(this.y);
  this.z = Math.ceil(this.z);

  return this;

};

/**
 * Apply Math.round to each component of this vector.
 * @return {Autodesk.Viewing.Math.Vector3} A reference to this vector.
 *
 * @alias Autodesk.Viewing.Math.Vector3#round
 */
LmvVector3.prototype.round = function () {

  this.x = Math.round(this.x);
  this.y = Math.round(this.y);
  this.z = Math.round(this.z);

  return this;

};

/**
 * Round each component toward zero (floor for positive, ceil for negative).
 * @return {Autodesk.Viewing.Math.Vector3} A reference to this vector.
 *
 * @alias Autodesk.Viewing.Math.Vector3#roundToZero
 */
LmvVector3.prototype.roundToZero = function () {

  this.x = this.x < 0 ? Math.ceil(this.x) : Math.floor(this.x);
  this.y = this.y < 0 ? Math.ceil(this.y) : Math.floor(this.y);
  this.z = this.z < 0 ? Math.ceil(this.z) : Math.floor(this.z);

  return this;

};

/**
 * Negate each component of this vector.
 * @return {Autodesk.Viewing.Math.Vector3} A reference to this vector.
 *
 * @alias Autodesk.Viewing.Math.Vector3#negate
 */
LmvVector3.prototype.negate = function () {

  this.x = -this.x;
  this.y = -this.y;
  this.z = -this.z;

  return this;

};

/**
 * Calculate the dot product of this vector and another.
 * @param {Autodesk.Viewing.Math.Vector3} v - The other vector.
 * @return {number} The dot product.
 *
 * @alias Autodesk.Viewing.Math.Vector3#dot
 */
LmvVector3.prototype.dot = function (v) {

  return this.x * v.x + this.y * v.y + this.z * v.z;

};

/**
 * Calculate the squared length of this vector.
 * @return {number} The squared length.
 *
 * @alias Autodesk.Viewing.Math.Vector3#lengthSq
 */
LmvVector3.prototype.lengthSq = function () {

  return this.x * this.x + this.y * this.y + this.z * this.z;

};

/**
 * Calculate the length of this vector.
 * @return {number} The length.
 *
 * @alias Autodesk.Viewing.Math.Vector3#length
 */
LmvVector3.prototype.length = function () {

  return Math.sqrt(this.x * this.x + this.y * this.y + this.z * this.z);

};

/**
 * Calculate the Manhattan length (L1 norm) of this vector.
 * @return {number} The Manhattan length.
 *
 * @alias Autodesk.Viewing.Math.Vector3#lengthManhattan
 */
LmvVector3.prototype.lengthManhattan = function () {

  return Math.abs(this.x) + Math.abs(this.y) + Math.abs(this.z);

};

/**
 * Normalize this vector to unit length.
 * @return {Autodesk.Viewing.Math.Vector3} A reference to this vector.
 *
 * @alias Autodesk.Viewing.Math.Vector3#normalize
 */
LmvVector3.prototype.normalize = function () {

  return this.divideScalar(this.length());

};

/**
 * Set the length of this vector. A zero vector will not be modified.
 * @param {number} l - The new length.
 * @return {Autodesk.Viewing.Math.Vector3} A reference to this vector.
 *
 * @alias Autodesk.Viewing.Math.Vector3#setLength
 */
LmvVector3.prototype.setLength = function (l) {

  var oldLength = this.length();

  if (oldLength !== 0 && l !== oldLength) {

    this.multiplyScalar(l / oldLength);

  }

  return this;

};

/**
 * Set this vector to the linear interpolation between this vector and another.
 * @param {Autodesk.Viewing.Math.Vector3} v - The target vector.
 * @param {number} alpha - The interpolation factor between 0 and 1.
 * @return {Autodesk.Viewing.Math.Vector3} A reference to this vector.
 *
 * @alias Autodesk.Viewing.Math.Vector3#lerp
 */
LmvVector3.prototype.lerp = function (v, alpha) {

  this.x += (v.x - this.x) * alpha;
  this.y += (v.y - this.y) * alpha;
  this.z += (v.z - this.z) * alpha;

  return this;

};

/**
 * Set this vector to the linear interpolation between two vectors.
 * @param {Autodesk.Viewing.Math.Vector3} v1 - The start vector.
 * @param {Autodesk.Viewing.Math.Vector3} v2 - The end vector.
 * @param {number} alpha - The interpolation factor between 0 and 1.
 * @return {Autodesk.Viewing.Math.Vector3} A reference to this vector.
 *
 * @alias Autodesk.Viewing.Math.Vector3#lerpVectors
 */
LmvVector3.prototype.lerpVectors = function (v1, v2, alpha) {

  this.subVectors(v2, v1).multiplyScalar(alpha).add(v1);

  return this;

};

/**
 * Set this vector to the cross product of this vector and another.
 * @param {Autodesk.Viewing.Math.Vector3} v - The other vector.
 * @note The second parameter 'w' is deprecated. We omit it from the documentation.
 * @return {Autodesk.Viewing.Math.Vector3} A reference to this vector.
 *
 * @alias Autodesk.Viewing.Math.Vector3#cross
 */
LmvVector3.prototype.cross = function (v, w) {

  if (w !== undefined) {

    console.warn('THREE.Vector3: .cross() now only accepts one argument. Use .crossVectors( a, b ) instead.');
    return this.crossVectors(v, w);

  }

  var x = this.x,y = this.y,z = this.z;

  this.x = y * v.z - z * v.y;
  this.y = z * v.x - x * v.z;
  this.z = x * v.y - y * v.x;

  return this;

};

/**
 * Set this vector to the cross product of two vectors.
 * @param {Autodesk.Viewing.Math.Vector3} a - The first vector.
 * @param {Autodesk.Viewing.Math.Vector3} b - The second vector.
 * @return {Autodesk.Viewing.Math.Vector3} A reference to this vector.
 *
 * @alias Autodesk.Viewing.Math.Vector3#crossVectors
 */
LmvVector3.prototype.crossVectors = function (a, b) {

  var ax = a.x,ay = a.y,az = a.z;
  var bx = b.x,by = b.y,bz = b.z;

  this.x = ay * bz - az * by;
  this.y = az * bx - ax * bz;
  this.z = ax * by - ay * bx;

  return this;

};

/**
 * Project this vector onto another vector.
 * @function
 * @param {Autodesk.Viewing.Math.Vector3} vector - The vector to project onto.
 * @return {Autodesk.Viewing.Math.Vector3} A reference to this vector.
 *
 * @alias Autodesk.Viewing.Math.Vector3#projectOnVector
 */
LmvVector3.prototype.projectOnVector = function () {

  var v1, dot;

  return function projectOnVector(vector) {

    if (v1 === undefined) v1 = new LmvVector3();

    v1.copy(vector).normalize();

    dot = this.dot(v1);

    return this.copy(v1).multiplyScalar(dot);

  };

}(),

/**
 * Project this vector onto a plane orthogonal to the given normal.
 * @function
 * @param {Autodesk.Viewing.Math.Vector3} planeNormal - The plane normal.
 * @return {Autodesk.Viewing.Math.Vector3} A reference to this vector.
 *
 * @alias Autodesk.Viewing.Math.Vector3#projectOnPlane
 */
LmvVector3.prototype.projectOnPlane = function () {

  var v1;

  return function projectOnPlane(planeNormal) {

    if (v1 === undefined) v1 = new LmvVector3();

    v1.copy(this).projectOnVector(planeNormal);

    return this.sub(v1);

  };

}(),

/**
 * Reflect this vector off a plane orthogonal to the given normal.
 * @function
 * @param {Autodesk.Viewing.Math.Vector3} normal - The plane normal (assumed to have unit length).
 * @return {Autodesk.Viewing.Math.Vector3} A reference to this vector.
 *
 * @alias Autodesk.Viewing.Math.Vector3#reflect
 */
LmvVector3.prototype.reflect = function () {

  // reflect incident vector off plane orthogonal to normal
  // normal is assumed to have unit length

  var v1;

  return function reflect(normal) {

    if (v1 === undefined) v1 = new LmvVector3();

    return this.sub(v1.copy(normal).multiplyScalar(2 * this.dot(normal)));

  };

}(),

/**
 * Calculate the distance from this vector to another.
 * @param {Autodesk.Viewing.Math.Vector3} v - The other vector.
 * @return {number} The distance.
 *
 * @alias Autodesk.Viewing.Math.Vector3#distanceTo
 */
LmvVector3.prototype.distanceTo = function (v) {

  return Math.sqrt(this.distanceToSquared(v));

};

/**
 * Calculate the squared distance from this vector to another.
 * @param {Autodesk.Viewing.Math.Vector3} v - The other vector.
 * @return {number} The squared distance.
 *
 * @alias Autodesk.Viewing.Math.Vector3#distanceToSquared
 */
LmvVector3.prototype.distanceToSquared = function (v) {

  var dx = this.x - v.x;
  var dy = this.y - v.y;
  var dz = this.z - v.z;

  return dx * dx + dy * dy + dz * dz;

};

/**
 * @deprecated Use Euler.setFromRotationMatrix() instead.
 * @private
 * @note Hidden as it is deprecated.
 */
LmvVector3.prototype.setEulerFromRotationMatrix = function (m, order) {

  console.error('THREE.Vector3: .setEulerFromRotationMatrix() has been removed. Use Euler.setFromRotationMatrix() instead.');

};

/**
 * @deprecated Use Euler.setFromQuaternion() instead.
 * @private
 * @note Hidden as it is deprecated.
 */
LmvVector3.prototype.setEulerFromQuaternion = function (q, order) {

  console.error('THREE.Vector3: .setEulerFromQuaternion() has been removed. Use Euler.setFromQuaternion() instead.');

};

/**
 * @deprecated Use setFromMatrixPosition() instead.
 * @param {Autodesk.Viewing.Math.Matrix4} m - The matrix.
 * @return {Autodesk.Viewing.Math.Vector3} A reference to this vector.
 *
 * @alias Autodesk.Viewing.Math.Vector3#getPositionFromMatrix
 * @private
 * @note Hidden as it is deprecated.
 */
LmvVector3.prototype.getPositionFromMatrix = function (m) {

  console.warn('THREE.Vector3: .getPositionFromMatrix() has been renamed to .setFromMatrixPosition().');

  return this.setFromMatrixPosition(m);

};

/**
 * @deprecated Use setFromMatrixScale() instead.
 * @param {Autodesk.Viewing.Math.Matrix4} m - The matrix.
 * @return {Autodesk.Viewing.Math.Vector3} A reference to this vector.
 *
 * @alias Autodesk.Viewing.Math.Vector3#getScaleFromMatrix
 * @private
 * @note Hidden as it is deprecated.
 */
LmvVector3.prototype.getScaleFromMatrix = function (m) {

  console.warn('THREE.Vector3: .getScaleFromMatrix() has been renamed to .setFromMatrixScale().');

  return this.setFromMatrixScale(m);

};

/**
 * @deprecated Use setFromMatrixColumn() instead.
 * @param {number} index - The column index.
 * @param {Autodesk.Viewing.Math.Matrix4} matrix - The matrix.
 * @return {Autodesk.Viewing.Math.Vector3} A reference to this vector.
 *
 * @alias Autodesk.Viewing.Math.Vector3#getColumnFromMatrix
 * @private
 * @note Hidden as it is deprecated.
 */
LmvVector3.prototype.getColumnFromMatrix = function (index, matrix) {

  console.warn('THREE.Vector3: .getColumnFromMatrix() has been renamed to .setFromMatrixColumn().');

  return this.setFromMatrixColumn(index, matrix);

};

/**
 * Set this vector from the position component of a 4x4 matrix.
 * @param {Autodesk.Viewing.Math.Matrix4} m - The matrix.
 * @return {Autodesk.Viewing.Math.Vector3} A reference to this vector.
 *
 * @alias Autodesk.Viewing.Math.Vector3#setFromMatrixPosition
 */
LmvVector3.prototype.setFromMatrixPosition = function (m) {

  this.x = m.elements[12];
  this.y = m.elements[13];
  this.z = m.elements[14];

  return this;

};

/**
 * Set this vector from the scale components of a 4x4 matrix.
 * @param {Autodesk.Viewing.Math.Matrix4} m - The matrix.
 * @return {Autodesk.Viewing.Math.Vector3} A reference to this vector.
 *
 * @alias Autodesk.Viewing.Math.Vector3#setFromMatrixScale
 */
LmvVector3.prototype.setFromMatrixScale = function (m) {

  var sx = this.set(m.elements[0], m.elements[1], m.elements[2]).length();
  var sy = this.set(m.elements[4], m.elements[5], m.elements[6]).length();
  var sz = this.set(m.elements[8], m.elements[9], m.elements[10]).length();

  this.x = sx;
  this.y = sy;
  this.z = sz;

  return this;

};

/**
 * Set this vector from a column of a matrix.
 * @param {number} index - The column index between 0 and 3.
 * @param {Autodesk.Viewing.Math.Matrix4} matrix - The matrix.
 * @return {Autodesk.Viewing.Math.Vector3} A reference to this vector.
 *
 * @alias Autodesk.Viewing.Math.Vector3#setFromMatrixColumn
 */
LmvVector3.prototype.setFromMatrixColumn = function (index, matrix) {

  var offset = index * 4;

  var me = matrix.elements;

  this.x = me[offset];
  this.y = me[offset + 1];
  this.z = me[offset + 2];

  return this;

};

/**
 * Check if this vector equals another vector.
 * @param {Autodesk.Viewing.Math.Vector3} v - The vector to compare.
 * @return {boolean} True if the vectors are equal.
 *
 * @alias Autodesk.Viewing.Math.Vector3#equals
 */
LmvVector3.prototype.equals = function (v) {

  return v.x === this.x && v.y === this.y && v.z === this.z;

};

/**
 * Set this vector from an array.
 * @param {number[]} array - The source array.
 * @param {number} [offset=0] - The offset into the array.
 * @return {Autodesk.Viewing.Math.Vector3} A reference to this vector.
 *
 * @alias Autodesk.Viewing.Math.Vector3#fromArray
 */
LmvVector3.prototype.fromArray = function (array, offset) {

  if (offset === undefined) offset = 0;

  this.x = array[offset];
  this.y = array[offset + 1];
  this.z = array[offset + 2];

  return this;

};

/**
 * Convert this vector to an array.
 * @param {number[]} [array=[]] - The target array.
 * @param {number} [offset=0] - The offset into the array.
 * @return {number[]} The array [x, y, z].
 *
 * @alias Autodesk.Viewing.Math.Vector3#toArray
 */
LmvVector3.prototype.toArray = function (array, offset) {

  if (array === undefined) array = [];
  if (offset === undefined) offset = 0;

  array[offset] = this.x;
  array[offset + 1] = this.y;
  array[offset + 2] = this.z;

  return array;

};

/**
 * Set this vector from a buffer attribute.
 * @param {THREE.BufferAttribute} attribute - The buffer attribute.
 * @param {number} index - The index in the attribute.
 * @param {number} [offset=0] - The offset.
 * @return {Autodesk.Viewing.Math.Vector3} A reference to this vector.
 *
 * @alias Autodesk.Viewing.Math.Vector3#fromAttribute
 * @private
 * @note Internal use only.
 */
LmvVector3.prototype.fromAttribute = function (attribute, index, offset) {

  if (offset === undefined) offset = 0;

  index = index * attribute.itemSize + offset;

  this.x = attribute.array[index];
  this.y = attribute.array[index + 1];
  this.z = attribute.array[index + 2];

  return this;
};

/**
 * Check if all components of a vector are finite.
 * @function
 * @param {Autodesk.Viewing.Math.Vector3} vec - The vector to check.
 * @return {boolean} True if all components are finite.
 *
 * @alias Autodesk.Viewing.Math.isVector3Finite
 */
export const isVector3Finite = (vec) => {
  return isFinite(vec.x) && isFinite(vec.y) && isFinite(vec.z);
};